import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createWarrantyClaim, findSoldLaptopForWarranty } from '@/services/warranties';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { Laptop } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';

interface WarrantyClaimFormProps {
  onSuccess: (claimId: string) => void;
  onCancel: () => void;
}

export const WarrantyClaimForm: React.FC<WarrantyClaimFormProps> = ({ onSuccess, onCancel }) => {
  const queryClient = useQueryClient();

  const [laptopSearch, setLaptopSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Laptop[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedLaptop, setSelectedLaptop] = useState<Laptop | null>(null);

  const [issue, setIssue] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSearchLaptops = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!laptopSearch.trim()) return;
    try {
      setIsSearching(true);
      const res = await findSoldLaptopForWarranty(laptopSearch);
      setSearchResults(res);
    } catch (err) {
      setSubmitError(parseDatabaseError(err));
    } finally {
      setIsSearching(false);
    }
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      if (!selectedLaptop) throw new Error('Please select a sold laptop for this claim.');
      if (!issue.trim()) throw new Error('Reported issue is required.');

      return await createWarrantyClaim({
        laptop_id: selectedLaptop.id,
        customer_id: selectedLaptop.sold_to_customer_id ?? undefined,
        issue: issue.trim(),
        status: 'open',
      });
    },
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.warranties.all });
      onSuccess(res.id);
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    createMutation.mutate();
  };

  const isWarrantyExpired = selectedLaptop?.warranty_expires
    ? new Date(selectedLaptop.warranty_expires) < new Date()
    : false;

  return (
    <form onSubmit={handleSubmit} className="space-y-4 max-h-[80vh] overflow-y-auto pr-1 text-xs">
      {submitError && <ErrorAlert message={submitError} />}

      {/* Step 1: Laptop Search */}
      <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-2">
        <label className="block font-bold text-brand-black uppercase tracking-wider">
          1. Find Sold Laptop <span className="text-brand-red">*</span>
        </label>
        <div className="flex gap-2">
          <input
            placeholder="Enter Stock ID, Serial #, Brand or Model..."
            value={laptopSearch}
            onChange={(e) => setLaptopSearch(e.target.value)}
            className="flex-1 px-3 py-1.5 bg-white border border-brand-border rounded text-xs"
          />
          <button
            type="button"
            onClick={handleSearchLaptops}
            disabled={isSearching || !laptopSearch.trim()}
            className="px-4 py-1.5 bg-brand-green text-white font-semibold rounded text-xs disabled:opacity-50"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>

        {searchResults.length > 0 && !selectedLaptop && (
          <div className="max-h-40 overflow-y-auto divide-y border rounded bg-white mt-2">
            {searchResults.map((lap) => (
              <div
                key={lap.id}
                onClick={() => setSelectedLaptop(lap)}
                className="p-2 flex items-center justify-between hover:bg-brand-bg cursor-pointer"
              >
                <div>
                  <span className="font-mono font-bold mr-2">{lap.stock_id}</span>
                  <span className="font-semibold">{lap.brand} {lap.model}</span>
                  <p className="text-[10px] text-brand-muted">
                    S/N: {lap.serial_number || 'N/A'} • Status: {lap.status}
                  </p>
                </div>
                <span className="text-[10px] font-bold text-brand-green">Select</span>
              </div>
            ))}
          </div>
        )}

        {selectedLaptop && (
          <div className="p-3 bg-white border border-brand-green/40 rounded-lg flex items-start justify-between mt-2">
            <div>
              <div className="flex items-center space-x-2">
                <span className="font-mono font-bold text-brand-black text-sm">{selectedLaptop.stock_id}</span>
                <span className="font-bold text-brand-black">{selectedLaptop.brand} {selectedLaptop.model}</span>
              </div>
              <p className="text-brand-muted mt-0.5">S/N: {selectedLaptop.serial_number || 'N/A'}</p>
              {selectedLaptop.warranty_expires ? (
                <p className={`mt-1 font-semibold flex items-center space-x-1 ${isWarrantyExpired ? 'text-red-700' : 'text-green-700'}`}>
                  {isWarrantyExpired ? <AlertTriangle className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                  <span>
                    Warranty Expiry: {new Date(selectedLaptop.warranty_expires).toLocaleDateString('en-PK')}
                    {isWarrantyExpired ? ' (EXPIRED)' : ' (VALID)'}
                  </span>
                </p>
              ) : (
                <p className="text-amber-800 font-medium mt-1">No warranty expiration date recorded</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setSelectedLaptop(null)}
              className="text-xs font-semibold text-brand-muted hover:text-brand-black"
            >
              Change
            </button>
          </div>
        )}
      </div>

      {/* Step 2: Issue Description */}
      {selectedLaptop && (
        <div className="bg-white p-3 rounded-lg border border-brand-border space-y-2">
          <label className="block font-bold text-brand-black uppercase tracking-wider">
            2. Reported Warranty Issue <span className="text-brand-red">*</span>
          </label>
          <textarea
            rows={3}
            placeholder="Describe the defect, battery degradation, screen flickering, or hardware failure..."
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded text-xs font-medium resize-none"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-end space-x-3 pt-3 border-t border-brand-border">
        <button type="button" onClick={onCancel} disabled={createMutation.isPending} className="px-4 py-2 font-medium text-brand-muted hover:text-brand-black">
          Cancel
        </button>
        <button
          type="submit"
          disabled={createMutation.isPending || !selectedLaptop || !issue.trim()}
          className="inline-flex items-center space-x-2 px-5 py-2 bg-brand-green text-white font-bold rounded-md hover:bg-brand-green-hover disabled:opacity-50 shadow-md"
        >
          {createMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          <span>{createMutation.isPending ? 'Opening Claim...' : 'Open Warranty Claim'}</span>
        </button>
      </div>
    </form>
  );
};
